import React, { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { formatBucket } from "../../utils/analyticsLabels";
import { alignPrevious, percentOf, previousNames, previousTotalOf, rateChange, seriesChange, sum } from "../../utils/analyticsSeries";
import BarChart from "../charts/BarChart";
import ChartFrame from "../charts/ChartFrame";
import DeltaChip from "../charts/DeltaChip";
import LineChart from "../charts/LineChart";
import ResponsiveChart from "../charts/ResponsiveChart";
import { formatCompact, formatCount, formatPercent } from "../charts/format";
import Segmented from "./Segmented";

// A evolucao (spec do redesign, R4.4): uma metrica de cada vez, dia a dia, com o periodo anterior a
// tracejado e o total dos dois na legenda. Com um anterior que os dados so cobrem em parte, a legenda diz
// "sem comparacao", e a linha tracejada fica: tem o nome de periodo anterior, e os valores dela sao os
// que houve. Cada separador passa as suas metricas (plano dos separadores, E4): `label` e a chave da
// traducao e `better` o sentido da variacao. Com uma so metrica nao ha seletor. So a da visao geral recebe
// o `anchorId`, o destino dos destaques.
// Um periodo de um dia vem por hora (RPAA-286, item 12, D11) e desenha-se em barras: hoje a cheio, a hora
// a decorrer mais tenue (com `partialLast`, a ultima com valor), as horas por chegar sem barra (vem a
// null, D8) e ontem em contorno no dia todo. A nota da barra mais tenue so aparece com ela: antes do
// primeiro valor do dia, ou com a hora a decorrer ainda a zero, nao ha nenhuma. O total do anterior na
// legenda e o cortado do backend (`previousTotalOf`), com o nome do `previousNames`
// ("Ontem ate as 17:30"); o desenho do anterior fica inteiro. Um resumo de antes do corte (D15) nao traz
// `previous_totals` nem `granularity`: o anterior soma-se e a serie le-se por dia.
// Uma metrica com `rate: { part, whole }` (RPAA-286, item 10, D12) e uma taxa de duas series, e nao uma
// serie: desenha a percentagem de cada balde (sem valor onde nao ha denominador, e nao um zero), a legenda
// diz a taxa de cada periodo (a do anterior, dos totais cortados das duas series), a variacao e em pontos,
// e nao tem pico: o maximo de uma taxa nao e um pico de procura.

// As metricas da visao geral. Os pedidos de operador nao sao bons nem maus: variacao neutra.
export const OVERVIEW_METRICS = [
  { key: "conversations", label: "analyticsConversations", better: "up" },
  { key: "customer_messages", label: "analyticsCustomerMessages", better: "up" },
  { key: "opens", label: "analyticsOpens", better: "up" },
  { key: "page_views", label: "analyticsPageViews", better: "up" },
  { key: "handoff_requests", label: "analyticsHandoffRequests", better: "neutral", needs: "operators" },
];
const HEIGHT = 240;

const EvolutionCard = ({ series, metrics, title, anchorId, partialLast, previousComplete, cut, timeZone }) => {
  const { translate, language } = useLanguage();
  const [metric, setMetric] = useState(metrics[0].key);
  // Uma metrica que deixa de existir (a capacidade saiu) volta a primeira, sem rebentar. Quem le o `needs`
  // e o separador, que filtra as metricas antes de as passar; aqui ignora-se.
  const chosen = metrics.find((item) => item.key === metric) || metrics[0];
  const { rate } = chosen;
  // A chave de uma taxa nao e uma serie: so as contagens a leem.
  const { current, previous } = rate
    ? {
        current: percentOf(series.metrics[rate.part].current, series.metrics[rate.whole].current),
        previous: percentOf(series.metrics[rate.part].previous, series.metrics[rate.whole].previous),
      }
    : series.metrics[chosen.key];
  const byHour = series.granularity === "hour";
  // A hora a decorrer e a ultima que chegou a serie: numa taxa, a do denominador (a taxa sem denominador tambem e null).
  const arrived = rate ? series.metrics[rate.whole].current : current;
  const runningHour = byHour && partialLast ? arrived.findLastIndex((value) => value !== null) : -1;
  // A nota fala da barra mais tenue: so aparece quando ela se desenha, e um zero nao tem barra.
  const fainterBarDrawn = runningHour >= 0 && current[runningHour] > 0;
  // Ontem nas mesmas horas, pela hora local de cada rotulo, e nao pelo indice (o spec ganha ao D9 do plano):
  // no dia em que a hora muda o indice desencontrava as horas a partir da mudanca.
  const alignedPrevious = alignPrevious(series, previous);
  const alignedPreviousBuckets = alignPrevious(series, series.previous_buckets);
  const points = series.buckets.map((bucket, index) => ({
    key: bucket,
    label: formatBucket(bucket, series.granularity, language),
    value: current[index],
    previous: alignedPrevious[index],
    partial: index === runningHour,
  }));
  const rateOfTotals = (part, whole) => (whole ? (100 * part) / whole : null);
  const total = rate ? rateOfTotals(sum(series.metrics[rate.part].current), sum(series.metrics[rate.whole].current)) : sum(current);
  const previousTotal = rate ? rateOfTotals(previousTotalOf(series, rate.part), previousTotalOf(series, rate.whole)) : previousTotalOf(series, chosen.key);
  const names = previousNames({ series, cut, timeZone, translate, language });
  // Vazio e nada a desenhar, e nao um total a zero: logo a seguir a meia-noite o anterior cortado e zero, e
  // ontem inteiro desenha-se.
  // Numa taxa um 0% com denominador e um valor: vazio e nenhum dos lados ter denominador.
  const empty = ![...current, ...previous].some((value) => (rate ? value !== null : value > 0));
  const count = (value) => (rate ? formatPercent(value, language) : formatCount(value, language));
  const tick = (value) => (rate ? `${formatCompact(value, language)}%` : formatCompact(value, language));
  const countOrBlank = (value) => (value === null ? "" : count(value));
  // Na legenda, o lado sem denominador diz que nao ha valor, e nao fica um negrito vazio.
  const legendValue = (value) => (value === null ? translate("analyticsNotAvailable") : count(value));
  // O ficheiro leva a taxa arredondada a uma casa, como o ecra a mostra, e nao o decimal sem fim.
  const inFile = (value) => (rate && value !== null ? Math.round(value * 10) / 10 : value);
  const unit = rate ? " (%)" : "";
  const table = {
    columns: [
      { key: "bucket", label: translate(byHour ? "analyticsHour" : "analyticsDay"), format: (bucket) => formatBucket(bucket, series.granularity, language) },
      { key: "value", label: `${translate("analyticsCurrentPeriod")}${unit}`, numeric: true, format: countOrBlank },
      // So no ficheiro: numa folha de calculo o valor anterior ao lado da data de agora lia-se como o desse dia.
      { key: "previousBucket", label: translate(byHour ? "analyticsPreviousPeriodHour" : "analyticsPreviousPeriodDay"), downloadOnly: true },
      { key: "previous", label: `${names.short}${unit}`, numeric: true, format: countOrBlank },
    ],
    rows: points.map((point, index) => ({ key: point.key, bucket: point.key, value: inFile(point.value), previousBucket: alignedPreviousBuckets[index], previous: inFile(point.previous) })),
  };
  return (
    <div id={anchorId}>
      <ChartFrame
        title={title}
        table={table}
        downloadName={`${title} ${translate(chosen.label)}`}
        empty={empty}
        actions={
          metrics.length > 1 && (
            <Segmented
              label={translate("analyticsMetric")}
              options={metrics.map((item) => ({ value: item.key, label: translate(item.label) }))}
              value={chosen.key}
              onChange={setMetric}
            />
          )
        }
      >
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs mb-2" style={{ color: "var(--color-text-secondary)" }}>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className={byHour ? "inline-block w-2.5 h-2.5 rounded-sm" : "inline-block w-3.5 h-0.5"} style={{ backgroundColor: "var(--chart-1)" }} />
            {translate("analyticsCurrentPeriod")}: <strong style={{ color: "var(--color-text-primary)" }}>{legendValue(total)}</strong>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className={byHour ? "inline-block w-2.5 h-2.5 rounded-sm border" : "inline-block w-3.5 border-t-2 border-dashed"}
              style={{ borderColor: "var(--chart-muted)" }}
            />
            {names.legend}: <strong style={{ color: "var(--color-text-primary)" }}>{legendValue(previousTotal)}</strong>
          </span>
          <DeltaChip
            change={
              rate
                ? rateChange({ series, part: rate.part, whole: rate.whole, better: chosen.better, previousComplete, language })
                : seriesChange({ series, key: chosen.key, better: chosen.better, previousComplete, language })
            }
          />
        </p>
        <ResponsiveChart height={HEIGHT}>
          {(width) =>
            byHour ? (
              <BarChart
                data={points}
                width={width}
                height={HEIGHT}
                colorOf={() => "var(--chart-1)"}
                label={translate(chosen.label)}
                previousLabel={names.short}
                partialLabel={translate("analyticsHourStillGoing")}
                formatValue={count}
                formatTick={tick}
                tickEvery={Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(width / 48))))}
              />
            ) : (
              <LineChart
                points={points}
                width={width}
                height={HEIGHT}
                label={translate(chosen.label)}
                previousLabel={names.short}
                formatValue={count}
                formatTick={tick}
                tickEvery={Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(width / 64))))}
                partialLast={partialLast}
                peakLabel={rate ? undefined : (point) => translate("analyticsPeak", { day: point.label, value: count(point.value) })}
              />
            )
          }
        </ResponsiveChart>
        {(byHour ? fainterBarDrawn : partialLast) && (
          <p className="text-xs mt-2" style={{ color: "var(--color-text-secondary)" }}>
            {translate(byHour ? "analyticsHourPartial" : "analyticsTodayPartial")}
          </p>
        )}
      </ChartFrame>
    </div>
  );
};

export default EvolutionCard;
